import { useEffect, useRef, useState } from "react";
import { SLIDES } from "./assets";
import Slide01Cover from "./slides/Slide01Cover";
import Slide02Positioning from "./slides/Slide02Positioning";
import Slide03BrandWorld from "./slides/Slide03BrandWorld";
import Slide04Customer from "./slides/Slide04Customer";
import Slide05Hero from "./slides/Slide05Hero";
import Slide06Materials from "./slides/Slide06Materials";
import Slide07WhyGCC from "./slides/Slide07WhyGCC";
import Slide08Retail from "./slides/Slide08Retail";
import Slide09Pricing from "./slides/Slide09Pricing";
import Slide10Contact from "./slides/Slide10Contact";

const SLIDE_COMPONENTS = [
  Slide01Cover,
  Slide02Positioning,
  Slide03BrandWorld,
  Slide04Customer,
  Slide05Hero,
  Slide06Materials,
  Slide07WhyGCC,
  Slide08Retail,
  Slide09Pricing,
  Slide10Contact,
];

const DARK_SLIDE_INDICES = new Set([0, 2, 6]); // Cover + Brand World + Why GCC

export default function Deck() {
  const deckRef = useRef(null);
  const [active, setActive] = useState(0);
  const [progress, setProgress] = useState(0);

  // Scroll tracking
  useEffect(() => {
    const el = deckRef.current;
    if (!el) return;
    const onScroll = () => {
      const max = el.scrollHeight - el.clientHeight;
      const p = max > 0 ? el.scrollTop / max : 0;
      setProgress(p);
      const idx = Math.round(el.scrollTop / el.clientHeight);
      setActive(Math.min(SLIDES.length - 1, Math.max(0, idx)));
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  // Keyboard nav
  useEffect(() => {
    const el = deckRef.current;
    if (!el) return;
    const onKey = (e) => {
      if (["ArrowDown", "PageDown", " "].includes(e.key)) {
        e.preventDefault();
        el.scrollTo({
          top: Math.min(SLIDES.length - 1, active + 1) * el.clientHeight,
          behavior: "smooth",
        });
      } else if (["ArrowUp", "PageUp"].includes(e.key)) {
        e.preventDefault();
        el.scrollTo({
          top: Math.max(0, active - 1) * el.clientHeight,
          behavior: "smooth",
        });
      } else if (e.key === "Home") {
        el.scrollTo({ top: 0, behavior: "smooth" });
      } else if (e.key === "End") {
        el.scrollTo({
          top: (SLIDES.length - 1) * el.clientHeight,
          behavior: "smooth",
        });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active]);

  const isDarkSlide = DARK_SLIDE_INDICES.has(active);

  return (
    <div className="relative bg-[var(--ais-cream)]">
      {/* Progress bar (informational only) */}
      <div
        className="fixed left-0 top-0 h-[2px] z-[60] bg-[var(--ais-gold)] transition-[width] duration-200"
        style={{ width: `${progress * 100}%` }}
        data-testid="progress-bar"
      />

      {/* AIS wordmark only — no other header text */}
      <header className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 md:px-12 py-6 md:py-7 pointer-events-none">
        <div
          className={`font-serif text-xl md:text-2xl tracking-[0.3em] select-none transition-colors duration-500 ${
            isDarkSlide
              ? "text-[var(--ais-cream)]"
              : "text-[var(--ais-espresso)]"
          }`}
        >
          AIS
        </div>
      </header>

      {/* Slide counter — informational only */}
      <div
        className={`fixed bottom-6 md:bottom-10 left-6 md:left-12 z-50 font-sans text-[0.7rem] tracking-luxe uppercase transition-colors duration-500 pointer-events-none ${
          isDarkSlide ? "text-[var(--ais-cream)]" : "text-[var(--ais-text)]"
        }`}
        data-testid="slide-counter"
      >
        <span className="font-serif text-3xl md:text-4xl italic align-middle mr-2">
          {String(active + 1).padStart(2, "0")}
        </span>
        <span className="opacity-60">/ {String(SLIDES.length).padStart(2, "0")}</span>
      </div>

      {/* Slides */}
      <div ref={deckRef} className="deck" data-testid="deck-scroller">
        {SLIDE_COMPONENTS.map((Comp, i) => (
          <Comp key={i} index={i} />
        ))}
      </div>
    </div>
  );
}
